import { AsyncPipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatTooltip } from '@angular/material/tooltip'
import {
  ElderDataToolbarComponent,
  ElderStopEventPropagationDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs'
import { switchMap } from 'rxjs/operators'
import { Property } from '../../../../../properties/property'
import { PropertyCache } from '../../../../../properties/property-cache'
import { ConstraintInputComponent } from '../../../../../shopfloor/workcenters/capability/constraints/constraint-input/constraint-input.component'
import { ConstraintInput } from '../../../../../shopfloor/workcenters/capability/constraints/constraintInput'
import { Configuration } from '../Configuration'
import {
  ConfigurationDialogComponent,
  IConfigurationDialogOptions,
} from '../configuration-dialog/configuration-dialog.component'
import { ConfigurationUtils } from '../configuration-utils'

@Component({
  selector: 'app-configuration-browser',
  templateUrl: './configuration-browser.component.html',
  styleUrls: ['./configuration-browser.component.scss'],
  providers: [PropertyCache],
  imports: [
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    MatSortHeader,
    MatTooltip,
    ConstraintInputComponent,
    ElderStopEventPropagationDirective,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class ConfigurationBrowserComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly _configurations$ = new BehaviorSubject<Configuration[]>([])
  public readonly configurationsPopulated$: Observable<Configuration[]>
  private dialog = inject(MatDialog)
  private propertyCache = inject(PropertyCache)

  @Output()
  public configurationsChange: EventEmitter<Configuration[]> = new EventEmitter<Configuration[]>()

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.configurationsPopulated$ = combineLatest([this._configurations$]).pipe(
      switchMap(([configs]) => {
        if (configs?.length > 0) {
          return this.populateConfigs(configs)
        } else {
          return of([])
        }
      })
    )
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set configurations(configs: Configuration[]) {
    this._configurations$.next(configs)
  }

  public get configurations(): Configuration[] {
    return this._configurations$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onConfigurationClicked(config: Configuration): void {
    const dialogRef = this.openDialog(config)
    dialogRef.afterClosed().subscribe((updatedCondition: Configuration) => {
      if (updatedCondition) {
        const conditionToUpdate = this.configurations.find(
          (c) => c.spec.id === updatedCondition.spec.id
        )
        ConfigurationUtils.update(updatedCondition, conditionToUpdate)
        this.configurationsChange.emit(this.configurations)
      }
    })
  }

  public addConfiguration(): void {
    const dialogRef = this.openDialog(new Configuration())
    dialogRef.afterClosed().subscribe((config: Configuration) => {
      if (config) {
        const configurations = [...this.configurations]
        configurations.push(config)
        this.configurations = configurations
        this.configurationsChange.emit(this.configurations)
      }
    })
  }

  public onRemoveConfiguration(toRemove: Configuration[]) {
    const configurations = [...this.configurations]
    toRemove.forEach((conf) => configurations.splice(configurations.indexOf(conf), 1))
    this.configurations = configurations
    this.configurationsChange.emit(this.configurations)
  }

  public onConfigurationConfUpdated(constraint: ConstraintInput, config: Configuration): void {
    config.conf.values = constraint.values
  }

  public buildConstraint(condition: Configuration): ConstraintInput {
    return new ConstraintInput(condition.spec, condition.conf.values)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private populateConfigs(configs: Configuration[]): Observable<Configuration[]> {
    return this.propertyCache.populateObjects(
      configs,
      (conf: Configuration) => conf.spec.propertyId,
      (conf: Configuration, prop: Property) => (conf.spec.property = prop)
    )
  }

  private openDialog(condition: Configuration): MatDialogRef<ConfigurationDialogComponent> {
    return this.dialog.open(ConfigurationDialogComponent, {
      minWidth: '30vw',
      height: '30vh',
      data: <IConfigurationDialogOptions>{
        config: condition,
      },
    })
  }
}
